import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSync } from '../../hooks/useSync';
import { toastService } from '../../services/toastService';
import SyncFormCard from '../../components/cards/SyncFormCard';
import { ICONS } from '../../constants/imageConstants';
import { Button } from '../../components/buttons/button';
import SyncPageSkeleton from '../../components/skeletons/SyncPageSkeleton';
import { useSyncableForms } from '../../hooks/useSyncableForms'; 
import DataListContainer from '../../components/layout/DataListContainer';
import PageLayout from '../../components/layout/PageLayout';

const SyncForms = () => {
  const navigate = useNavigate();
  const { isOnline, syncInProgress, manualSync } = useSync();
  const [selectedForms, setSelectedForms] = useState([]);

  const {
    formsToSync,
    totalPendingCount,
    outboxItems,
    isLoading
  } = useSyncableForms();

  const handleSelectForm = useCallback((formId) => {
    setSelectedForms(prev =>
      prev.includes(formId)
        ? prev.filter(id => id !== formId)
        : [...prev, formId]
    );
  }, []);

  const handleViewList = useCallback((formId) => navigate(`/forms/data?formId=${formId}`, { state: { from: '/sync', filter: 'pending' } }), [navigate]);

  const [syncingFormId, setSyncingFormId] = useState(null);

  const handleSyncNow = useCallback((formId) => {
    const itemsToSync = (outboxItems || []).filter(item => String(item.payload?.form_id) === String(formId));
    if (itemsToSync.length > 0) {
      setSyncingFormId(formId);
      manualSync({ items: itemsToSync, showToast: true })
        .finally(() => setSyncingFormId(null));
    } else {
      toastService.info('No pending items for this form to sync.');
    }
  }, [outboxItems, manualSync]);

  const handleSyncAction = useCallback(() => {
    if (totalPendingCount === 0) {
      toastService.info('Everything is up to date.');
      return;
    }
    const itemsToSync = selectedForms.length > 0
      ? (outboxItems || []).filter(item => selectedForms.includes(String(item.payload?.form_id)))
      : undefined;

    if (selectedForms.length > 0 && (!itemsToSync || itemsToSync.length === 0)) {
        toastService.info('Selected forms have no pending items to sync.');
        return;
    }

    manualSync({ items: itemsToSync, showToast: true });
  }, [totalPendingCount, selectedForms, outboxItems, manualSync]);

  const syncButtonText = selectedForms.length > 0 ? `Sync Selected` : `Sync All`;

  return (
    <PageLayout title="Sync Status" showBackButton showStatus>
      <DataListContainer
        isLoading={isLoading}
        data={formsToSync}
        skeletonComponent={<SyncPageSkeleton />}
        emptyStateProps={{
          title: "No Submissions to Sync",
          message: "All submissions have been synced with the server."
        }}
      >
        <div className="space-y-3">
          {formsToSync.map(form => (
            <SyncFormCard
              key={form.id}
              title={form.title}
              description={form.description}
              pendingCount={form.pendingCount}
              onViewList={() => handleViewList(form.id)}
              onSyncNow={() => handleSyncNow(form.id)}
              onSelect={() => handleSelectForm(String(form.id))}
              isSelected={selectedForms.includes(String(form.id))}
              isSyncing={syncingFormId === form.id}
            />
          ))}
        </div>
      </DataListContainer>

      {!isLoading && totalPendingCount > 0 && (
        <div className="mt-6">
          <Button
            fullWidth
            variant="primary"
            size="lg"
            onClick={handleSyncAction}
            disabled={!isOnline || syncInProgress}
          >
            <img 
              src={ICONS.SYNC} 
              alt="" 
              className={`mr-2 h-5 w-5 ${syncInProgress ? 'animate-spin' : ''}`}
            />
            {syncInProgress ? 'Syncing...' : syncButtonText}
          </Button>
        </div>
      )}
    </PageLayout>
  );
};

export default SyncForms;